import { RoastingSeries } from "../../input/roastingSeriesTypes";
export interface BTCurveSimilarityResult {
  seriesId: string;
  distance: number;
  similarityScore: number;
}

interface BTCurveSimilarityOptions {
  sampleCount?: number; // 기본 30
  normalizeBT?: boolean;
}

//BT 곡선 샘플링
function resampleBTCurve(series: RoastingSeries, startTime: number, endTime: number, sampleCount: number): number[] {
  const points = series.series.filter((p) => p.time >= startTime && p.time <= endTime);

  if (points.length < 2) return [];

  const result: number[] = [];
  const total = endTime - startTime;

  for (let i = 0; i < sampleCount; i++) {
    const t = startTime + (i / (sampleCount - 1)) * total;

    const idx = points.findIndex((p) => p.time >= t);
    if (idx <= 0) {
      result.push(points[0].bt);
    } else {
      const p0 = points[idx - 1];
      const p1 = points[idx];
      const ratio = (t - p0.time) / (p1.time - p0.time);
      result.push(p0.bt + ratio * (p1.bt - p0.bt));
    }
  }

  return result;
}

//BT 정규화
function normalizeVector(values: number[]): number[] {
  const min = Math.min(...values);
  const max = Math.max(...values);

  if (max === min) return values.map(() => 0);

  return values.map((v) => (v - min) / (max - min));
}

//BT 곡선 거리 계산
function l2Distance(a: number[], b: number[]): number {
  let sum = 0;

  for (let i = 0; i < a.length; i++) {
    const d = a[i] - b[i];
    sum += d * d;
  }

  return Math.sqrt(sum / a.length);
}

//현재 로스팅 vs 과거 로스팅 유사도 계산
export function computeBTCurveSimilarity(
  current: RoastingSeries,
  past: RoastingSeries,
  options: BTCurveSimilarityOptions = {}
): number {
  const { sampleCount = 30, normalizeBT = true } = options;

  if (current.dryEndTime == null || past.dryEndTime == null) {
    return Infinity;
  }

  const currentEnd = current.series.at(-1)?.time ?? current.dryEndTime;

  const currVec = resampleBTCurve(current, current.dryEndTime, currentEnd, sampleCount);

  const pastVec = resampleBTCurve(
    past,
    past.dryEndTime,
    past.dryEndTime + (currentEnd - current.dryEndTime),
    sampleCount
  );

  if (currVec.length === 0 || pastVec.length === 0) {
    return Infinity;
  }

  const a = normalizeBT ? normalizeVector(currVec) : currVec;
  const b = normalizeBT ? normalizeVector(pastVec) : pastVec;

  return l2Distance(a, b);
}

//pastSeries에서 가장 유사한 것 선택
export function findMostSimilarBTCurve(
  current: RoastingSeries,
  pastSeriesList: RoastingSeries[],
  options?: BTCurveSimilarityOptions
): BTCurveSimilarityResult | undefined {
  let best: BTCurveSimilarityResult | undefined;

  for (const past of pastSeriesList) {
    const dist = computeBTCurveSimilarity(current, past, options);

    if (!best || dist < best.distance) {
      best = {
        seriesId: past.id!,
        distance: dist,
        similarityScore: 1 / (1 + dist),
      };
    }
  }

  return best;
}
